import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
    Modal, 
    Form, 
    Input, 
    Checkbox, 
    Button, 
    Row, 
    Col, 
    Spin, 
    Alert 
} from 'antd';
import withRoleAccess from '../../hoc/withRoleAccess';

const EditTeacherModal = ({ isOpen, onClose, onSave, teacherId }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        phone: '',
        address: '',
        email: '',
        nickname: '',
        photourl: '',
        bachelor_degree: '',
        specialize_subjects: '',
        schedule: '',
        isparttime: false,
        bachelor_school: '',
        master_degree: '',
        master_school: '',
        doctoral_degree: '',
        doctoral_school: '',
        hoursrate: '',
        color: '',
        language: '',
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchTeacherData = async () => {
            if (!teacherId) return;
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/teachers/${teacherId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setFormData(response.data);
                form.setFieldsValue(response.data);
            } catch (error) {
                console.error('Error fetching teacher data:', error);
                setError(t('fetchTeacherError'));
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchTeacherData();
        }
    }, [teacherId, isOpen, API_URL, t, form]);

const handleSubmit = async (values) => {
    try {
        const token = localStorage.getItem('token');
        
        // Exclude color and language from being sent
        const { color, language, ...formDataToSubmit } = values;
        
        // Only include fields that have values
        const sanitizedData = Object.entries(formDataToSubmit).reduce((acc, [key, value]) => {
            if (value !== undefined) {
                acc[key] = value || null;
            }
            return acc;
        }, {});
        
        // Send the sanitized data to the API
        await axios.put(`${API_URL}/teachers/${teacherId}`, sanitizedData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        
        // Call onSave with teacherId included so parent component has it
        onSave({ 
            ...sanitizedData, 
            color,
            teacherid: teacherId,  // ⭐ ADD THIS!
            userid: teacherId      // ⭐ ADD THIS TOO (since userid = teacherid)
        });
        
        onClose();
    } catch (error) {
        console.error('Error updating teacher:', error);
        setError(t('updateTeacherError'));
    }
};
    return (
        <Modal
            title={t('editTeacher')}
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={800}
            destroyOnClose
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                </div>
            ) : error ? (
                <Alert message={error} type="error" showIcon />
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={formData}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label={t('firstName')}
                                name="firstname"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={t('lastName')}
                                name="lastname"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label={t('email')}
                                name="email"
                            >
                                <Input type="email" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={t('nickname')}
                                name="nickname"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label={t('color')}
                                name="color"
                            >
                                <Input 
                                    readOnly 
                                    style={{ 
                                        backgroundColor: formData.color, 
                                        color: '#fff'
                                    }} 
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={t('language')}
                                name="language"
                            >
                                <Input 
                                    value={Array.isArray(formData.language) ? formData.language.join(', ') : formData.language}
                                    readOnly
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label={t('photoURL')}
                                name="photourl"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={t('hoursRate')}
                                name="hoursrate"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label={t('specializeSubjects')}
                                name="specialize_subjects"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={t('about')}
                                name="schedule"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label={t('bachelorDegree')}
                                name="bachelor_degree"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={t('bachelorSchool')}
                                name="bachelor_school"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label={t('masterDegree')}
                                name="master_degree"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={t('masterSchool')}
                                name="master_school"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label={t('doctoralDegree')}
                                name="doctoral_degree"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={t('doctoralSchool')}
                                name="doctoral_school"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="isparttime"
                        valuePropName="checked"
                    >
                        <Checkbox>{t('partTime')}</Checkbox>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <Button onClick={onClose}>
                                {t('cancel')}
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {t('save')}
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            )}
        </Modal>
    );
};

export default withRoleAccess(EditTeacherModal, ['admin', 'superadmin']);